import { useEffect, useRef, useState } from 'react';
import { Button, type ButtonProps } from 'frosted-ui';
import { Copy16, Checkmark16 } from '@frosted-ui/icons';

interface CopyButtonProps {
  text: string;
  label?: string;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API blocked (e.g. iframe permissions) — fall back to a hidden textarea.
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

export default function CopyButton({ text, label = 'Copy link', size = '2', variant = 'solid' }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  async function handleCopy() {
    if (await copyText(text)) {
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <Button type="button" size={size} variant={variant} onClick={handleCopy} style={{ flexShrink: 0 }}>
      {copied ? <Checkmark16 /> : <Copy16 />}
      {copied ? 'Copied' : label}
    </Button>
  );
}
